import React from 'react';
import { BirdType } from '../types';

interface BirdCardProps {
  bird: BirdType;
  isSelected?: boolean;
  onClick?: () => void;
  disabled?: boolean;
  validationErrors?: string[];
  estimatedCost?: number;
  estimatedTime?: string;
}

const BirdCard: React.FC<BirdCardProps> = ({
  bird,
  isSelected = false,
  onClick,
  disabled = false,
  validationErrors = [],
  estimatedCost,
  estimatedTime
}) => {
  const hasErrors = validationErrors.length > 0;

  return (
    <div
      className={`relative bird-card rounded-lg border-2 p-4 cursor-pointer transition-all duration-200 ${
        disabled
          ? 'border-gray-200 bg-gray-50 cursor-not-allowed opacity-60'
          : hasErrors
          ? 'border-red-300 bg-red-50 hover:border-red-400'
          : isSelected
          ? 'border-blue-500 bg-blue-50 shadow-md'
          : 'border-gray-200 bg-white hover:border-blue-300 hover:shadow-md'
      }`}
      onClick={!disabled ? onClick : undefined}
    >
      {/* Availability indicator */}
      {!bird.availability && (
        <div className="absolute top-2 right-2 bg-red-500 text-white text-xs px-2 py-1 rounded-full">
          Unavailable
        </div>
      )}

      {/* Bird info */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1">
          <h3 className="text-lg font-semibold text-gray-900 mb-1">
            {bird.name}
          </h3>
          <p className="text-sm text-gray-500 italic mb-2">
            {bird.species}
          </p>
        </div>
        <div className="text-2xl ml-2">🦅</div>
      </div>

      {/* Pricing */}
      <div className="mb-4 p-3 bg-gray-50 rounded-lg">
        <div className="flex justify-between items-center text-sm mb-2">
          <span className="text-gray-500">Base cost:</span>
          <span className="font-medium text-gray-900">${bird.properties.baseCost.toFixed(2)}</span>
        </div>
        <div className="flex justify-between items-center text-xs text-gray-500">
          <span>+ ${bird.properties.costPerKm}/km</span>
          <span>+ ${bird.properties.costPerCharacter}/char</span>
        </div>
      </div>

      {/* Description */}
      <p className="text-sm text-gray-600 mb-4 line-clamp-2">
        {bird.description}
      </p>

      {/* Properties grid */}
      <div className="grid grid-cols-2 gap-3 text-xs mb-4">
        <div className="flex justify-between">
          <span className="text-gray-500">Speed:</span>
          <span className="font-medium text-gray-900">{bird.properties.speed} km/h</span>
        </div>
        <div className="flex justify-between">
          <span className="text-gray-500">Range:</span>
          <span className="font-medium text-gray-900">{bird.properties.maxDistance}km</span>
        </div>
        <div className="flex justify-between">
          <span className="text-gray-500">Max Msg:</span>
          <span className="font-medium text-gray-900">{bird.properties.maxMessageLength} chars</span>
        </div>
        <div className="flex justify-between">
          <span className="text-gray-500">Reliability:</span>
          <span className="font-medium text-gray-900">{bird.properties.reliability}%</span>
        </div>
      </div>

      {/* Estimated cost and time */}
      {estimatedCost !== undefined && estimatedTime && (
        <div className="border-t pt-3 mb-3 bg-green-50 -m-4 p-4 rounded-b-lg">
          <div className="flex justify-between items-center text-sm font-medium text-green-800">
            <span>Estimated cost:</span>
            <span>${estimatedCost.toFixed(2)}</span>
          </div>
          <div className="flex justify-between items-center text-xs text-green-600">
            <span>Delivery time:</span>
            <span>{estimatedTime}</span>
          </div>
        </div>
      )}

      {/* Risk warnings */}
      {hasErrors && (
        <div className="border-t pt-3 -m-4 p-4 bg-amber-50 rounded-b-lg border-amber-200">
          {validationErrors.map((warning, index) => (
            <div key={index} className="flex items-start text-xs text-amber-700">
              <svg className="w-3 h-3 mr-1 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              <span>{warning}</span>
            </div>
          ))}
        </div>
      )}

      {/* Selection indicator */}
      {isSelected && (
        <div className={`absolute top-2 left-2 text-white rounded-full p-1 ${
          hasErrors ? 'bg-amber-500' : 'bg-blue-500'
        }`}>
          <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
          </svg>
        </div>
      )}
    </div>
  );
};

export default BirdCard;
